import * as stylex from "@stylexjs/stylex";
import type { ElementType, HTMLAttributes, ReactNode } from "react";
import "../tokens.stylex.ts";
import { color, font, fontSize, fontWeight, lineHeight } from "../tokens.stylex.ts";
type Size = "xs" | "sm" | "md" | "lg";
type Tone = "primary" | "secondary" | "tertiary" | "accent" | "success" | "warning" | "error" | "inverse";
export interface TextProps extends Omit<HTMLAttributes<HTMLElement>, "className" | "style"> {
  as?: ElementType;
  size?: Size;
  tone?: Tone;
  weight?: "regular" | "medium" | "semibold";
  mono?: boolean;
  truncate?: boolean;
  tabular?: boolean;
  children?: ReactNode;
}
export function Text({
  as: Component = "span",
  size = "md",
  tone = "primary",
  weight = "regular",
  mono = false,
  truncate = false,
  tabular = false,
  ...rest
}: TextProps) {
  return <Component {...rest} {...stylex.props(styles.base, sizes[size], tones[tone], weights[weight], mono && styles.mono, truncate && styles.truncate, tabular && styles.tabular)} />;
}
export interface HeadingProps extends Omit<HTMLAttributes<HTMLHeadingElement>, "className" | "style"> {
  level?: 1 | 2 | 3;
  children?: ReactNode;
}
export function Heading({
  level = 2,
  ...rest
}: HeadingProps) {
  const Component = `h${level}` as const;
  return <Component {...rest} {...stylex.props(styles.heading, headings[level])} />;
}
const styles = {
  base: {
    kMv6JI: "x1wv6yj",
    kLWn49: "x9tub0y",
    kogj98: "x1ghz6dp",
    $$css: true
  },
  mono: {
    kMv6JI: "xxg9qwl",
    kGuDYH: "x1wx7ac5",
    kb6lSQ: "xjat59b",
    $$css: true
  },
  truncate: {
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    khDVqt: "xuxw1ft",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  tabular: {
    kcqcaj: "xss6m8b",
    $$css: true
  },
  heading: {
    kMv6JI: "x1wv6yj",
    kMwMTN: "x9aqtqa",
    k63SB2: "x1ivwu1z",
    kb6lSQ: "x1lg07ej",
    kogj98: "x1ghz6dp",
    $$css: true
  }
};
const headings = {
  "1": {
    kGuDYH: "x8ygwpf",
    kLWn49: "xfpseah",
    $$css: true
  },
  "2": {
    kGuDYH: "x1p23z6u",
    kLWn49: "x1yfojzp",
    $$css: true
  },
  "3": {
    kGuDYH: "xluwven",
    kLWn49: "x1yfojzp",
    $$css: true
  }
};
const sizes = {
  xs: {
    kGuDYH: "xmccgnw",
    $$css: true
  },
  sm: {
    kGuDYH: "xket8oi",
    $$css: true
  },
  md: {
    kGuDYH: "xluwven",
    $$css: true
  },
  lg: {
    kGuDYH: "x1p23z6u",
    $$css: true
  }
};
const weights = {
  regular: {
    k63SB2: "x14axpdx",
    $$css: true
  },
  medium: {
    k63SB2: "x1219144",
    $$css: true
  },
  semibold: {
    k63SB2: "x1ivwu1z",
    $$css: true
  }
};
const tones = {
  primary: {
    kMwMTN: "x9aqtqa",
    $$css: true
  },
  secondary: {
    kMwMTN: "x187ovcu",
    $$css: true
  },
  tertiary: {
    kMwMTN: "xxe3v3u",
    $$css: true
  },
  accent: {
    kMwMTN: "x1rizfjd",
    $$css: true
  },
  success: {
    kMwMTN: "x1oux70d",
    $$css: true
  },
  warning: {
    kMwMTN: "x1cvnich",
    $$css: true
  },
  error: {
    kMwMTN: "x1xcodwn",
    $$css: true
  },
  inverse: {
    kMwMTN: "x14f1h33",
    $$css: true
  }
};